import { useCallback, useEffect, useRef, useState } from 'react';
import { formatCommittedNumber, parseNumberDraft } from './commitOnBlurNumber';

export function useCommitOnBlurNumber({
  value,
  min,
  max,
  onCommit,
}: {
  value: number;
  min?: number;
  max?: number;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(() => formatCommittedNumber(value));
  const [editing, setEditing] = useState(false);
  const skipNextBlurCommit = useRef(false);
  const valueRef = useRef(value);
  const draftRef = useRef(draft);
  const onCommitRef = useRef(onCommit);
  const minRef = useRef(min);
  const maxRef = useRef(max);
  valueRef.current = value;
  draftRef.current = draft;
  onCommitRef.current = onCommit;
  minRef.current = min;
  maxRef.current = max;

  useEffect(() => {
    if (!editing) {
      setDraft(formatCommittedNumber(value));
    }
  }, [editing, value]);

  const commit = useCallback(() => {
    const parsed = parseNumberDraft(draftRef.current, {
      min: minRef.current,
      max: maxRef.current,
    });
    setEditing(false);
    setDraft(formatCommittedNumber(parsed));
    if (parsed !== valueRef.current) {
      onCommitRef.current(parsed);
    }
  }, []);

  const revert = useCallback(() => {
    skipNextBlurCommit.current = true;
    setEditing(false);
    setDraft(formatCommittedNumber(valueRef.current));
  }, []);

  return {
    value: draft,
    min,
    max,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditing(true);
      setDraft(event.target.value);
    },
    onFocus: () => {
      setEditing(true);
    },
    onBlur: () => {
      if (skipNextBlurCommit.current) {
        skipNextBlurCommit.current = false;
        setEditing(false);
        setDraft(formatCommittedNumber(valueRef.current));
        return;
      }
      commit();
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        revert();
        event.currentTarget.blur();
      }
    },
  };
}
